import { createTransform } from 'redux-persist';
import { PersistConfig } from 'redux-persist/es/types';
import storage from 'redux-persist/lib/storage';

import {
    AppURLSearchParams, AuthData, AuthState, INITIAL_AUTH_STATE
} from '../../../auth';
import { AppState } from '../../app.types';
import { ApplicationState } from '../../state/app.types';
import { isNonEmptyString } from '@utils';

/**
 * This contains the business rules about persisting the Redux state to localstorage for eg refreshing the browser.
 * The application has all it's state in Redux and so there are some things that we don't want to
 * store between sessions and some that we do. Here we can write conditions for what is needed in the in and out transforms
 * that end up in the local storage and then restored to the session.
 * We are not using every feature of this library and you could make a smaller bundle by porting this to custom
 * middleware using localStorage apis directly.
 *
 * @see https://github.com/rt2zz/redux-persist#transforms
 */
export const persistAppState = createTransform<ApplicationState, ApplicationState>(
    (inboundState) => inboundState,
    (outboundState) => {
        return {
            ...outboundState,
            // Dont restore the isInitialized flag so auth still initializes
            isInitialized: false,
            // Locale needs to be loaded via http
            localeLoaded: null,
            // access will be checked from the user profile
            userHasProductAccess: false,
            // Dynamic Params do not persist between sessions
            dynamicParams: {}
        };
    },
    { whitelist: ['app'] }
);

function parseTimeout (timeOut: string): number {
    const [days, hours, minutes, seconds] = timeOut.split(':').map(Number);

    return ((days * 24 + hours) * 60 + minutes) * 60 * 1000 + (seconds || 0) * 1000;
}

export const createPersistAuthState = createTransform<AuthState, AuthState>(
    (inboundState) => {
        // Only persist the tokens and only if there is no error.
        if (!inboundState.isError) {
            const { data } = inboundState;

            if (data?.createdAt && data?.timeOut) {
                const createdAt = new Date(data.createdAt).getTime();
                const timeOutMs = parseTimeout(data.timeOut);
                const expiresAt = createdAt + timeOutMs;

                if (expiresAt <= Date.now()) {
                    return INITIAL_AUTH_STATE;
                }
            }

            return inboundState;
        }

        return INITIAL_AUTH_STATE;
    },
    (outboundState) => {
        // Before the app bootstraps and we perform hydration of the redux state,
        // let the token take priority in the url.
        // This is later removed by deleteUrlParams() method.
        const url = new URL(location.href);
        const token = url.searchParams.get(AppURLSearchParams.token);
        const jwtToken = url.searchParams.get(AppURLSearchParams.jwtToken);

        if (isNonEmptyString(jwtToken)) {
            return createParamAuthState({ jwtToken });
        }

        if (isNonEmptyString(token)) {
            return createParamAuthState({ token });
        }

        return outboundState;
    },
    { whitelist: ['auth', 'configuration'] }
);

function createParamAuthState (data: AuthData): AuthState {
    return {
        ...INITIAL_AUTH_STATE,
        isSuccess: true,
        data: data as AuthState['data']
    };
}

export const persistConfig: PersistConfig<AppState> = {
    key: 'root',
    storage,
    whitelist: ['auth', 'app', 'configuration'],
    transforms: [
        persistAppState,
        createPersistAuthState
    ]
};
